import React, { useCallback, useState, useMemo, useEffect, useRef } from 'react';
import { useHyperFormula } from '../../hooks/useHyperFormula';
import numeral from 'numeral';
import { handleXBRLFieldBlur } from '../../utils/xbrlMandatoryFields';
import { ValidationIndicator } from '../ValidationIndicator';


// Attributable fields configuration (centralized)
const ATTRIBUTABLE_FIELDS = [
  'ProfitLossAttributableToOwnersOfParent',
  'ProfitLossAttributableToOwnersOfCompany',
  'ProfitLossAttributableToOtherComponentsOfEquity', 
  'ProfitLossAttributableToNoncontrollingInterests',
  'ComprehensiveIncomeAttributableToOwnersOfParent',
  'ComprehensiveIncomeAttributableToNoncontrollingInterests'
];

const ATTRIBUTABLE_FIELD_NAMES = ATTRIBUTABLE_FIELDS.map(field => `Attributable-${field}`);

// Fields that should NOT have footnote buttons (calculated/special fields)
const FIELDS_WITHOUT_FOOTNOTE = [
  'ProfitLoss',
  'ComprehensiveIncome',
  'CashAndCashEquivalents'
];

// Cash Flow fields configuration
const CASH_FLOW_FIELDS = [
  'CashAndCashEquivalents'
] as const;

const CASH_FLOW_LABELS: Record<string, Record<number, string>> = {
  'CashAndCashEquivalents': {
    1: 'Cash and cash equivalents at beginning of period',
    2: 'Cash and cash equivalents at end of period'
  }
};

interface Column {
  label: string;
  member: string;
  period: string;
}

interface AnalysisRowConfig {
  label: string;
  sourceFieldName: string;
  mirroredFieldName: string;
  computeFromFieldNames?: string[];
}

interface AnalysisSectionConfig {
  headerLabel: string;
  rows: AnalysisRowConfig[];
}


interface LineItemProps {
  item: any;
  columns: Column[];
  level: number;
  negativeTargets: string[];
  readOnlyFields: Set<string>;
  companyInfo: any;
  formData: Record<string, string>;
  onFieldChange: (name: string, contextRef: string, value: string) => void;
  onGetCalculatedValue: (fieldName: string, contextRef: string) => number | null;
  getTestOccurrence?: (itemName: string, itemKey: string) => number; // For testing occurrence
  itemIndex?: number; // Add index to make unique keys
  onFootnoteClick?: (
    fieldLabel: string,
    fieldName: string,
    contextRef: string,
    qname: string,
    fieldId: string,
    isReadonly: boolean
  ) => void;
  // Cross-field navigation props
  duplicateFields?: Set<string>;
  onShowNavigationMenu?: (fieldName: string, sectionId: string, triggerElement: HTMLElement) => void;
  sectionId?: string;
  // hasFieldError/getFieldError removed - validation now handled via DOM manipulation
}

// Helper to format date strings (remove dashes for context refs, or return as-is if already formatted)
const formatDateForContext = (dateStr: string): string => {
  // If date is already in YYYYMMDD format, return as-is
  if (/^\d{8}$/.test(dateStr)) {
    return dateStr;
  }
  // Otherwise remove dashes
  return dateStr.replace(/-/g, '');
};

// Helper function to get REAL XBRL context ref (for data-context-ref)
// Builds real XBRL context refs: asof_YYYYMMDD_Member or fromto_YYYYMMDD_YYYYMMDD_Member
const getPeriodContext = (item: any, companyInfo: any, member: string, period: string): string => {
  const cleanMember = member.startsWith('_') ? member.substring(1) : member;
  const memberSuffix = cleanMember ? `_${cleanMember}` : '';
  
  // Get the appropriate period info
  const periodInfo = period === 'current' 
    ? (companyInfo.reporting_period || companyInfo.currentPeriod)
    : (companyInfo.previous_period || companyInfo.previousPeriod);
  
  if (!periodInfo) {
    // Fallback if period info not available
    return `${period}${memberSuffix}`;
  }
  
  const formattedStart = formatDateForContext(periodInfo.start);
  const formattedEnd = formatDateForContext(periodInfo.end);
  
  // Build context ref based on period_type (matches XBRL standard)
  if (item.properties?.period_type === 'duration') {
    // Duration period: fromto_START_END_Member
    return `fromto_${formattedStart}_${formattedEnd}${memberSuffix}`;
  } else {
    // Instant period (default): asof_END_Member
    return `asof_${formattedEnd}${memberSuffix}`;
  }
};

// Helper function to get STANDARD context (for data-year-end / HyperFormula)
// Converts real context to standard: current_SeparateMember, comparative_SeparateMember, etc.
// This is what HyperFormula uses for calculations (performance optimization)
const getStandardContext = (realContext: string, column: Column): string => {
  // Extract member from real context (last part after underscore)
  const parts = realContext.split('_');
  const member = parts[parts.length - 1]; // SeparateMember, ConsolidatedMember, etc.
  
  // Determine if current or comparative based on column period
  const period = column.period === 'current' ? 'current' : 'comparative';
  
  // Build standard context
  return `${period}_${member}`;
};

// Helper function to get current opening period context (matches get_current_opening_period_context macro)
// Used for Cash Flow opening fields (occurrence 1) in current column
const getCurrentOpeningPeriodContext = (item: any, companyInfo: any, member: string): string => {
  const period = companyInfo.currentPeriod || companyInfo.reporting_period;
  if (!period) {
    const cleanMember = member.startsWith('_') ? member.substring(1) : member;
    const memberSuffix = cleanMember ? `_${cleanMember}` : '';
    return `context${memberSuffix}`;
  }

  const formattedOpening = formatDateForContext(period.opening);
  const cleanMember = member.startsWith('_') ? member.substring(1) : member;
  const memberSuffix = cleanMember ? `_${cleanMember}` : '';

  return `asof_${formattedOpening}${memberSuffix}`;
};

// Helper function to get comparative opening period context (matches get_comparative_opening_period_context macro)
// Used for Cash Flow opening fields (occurrence 1) in previous/comparative column
const getComparativeOpeningPeriodContext = (item: any, companyInfo: any, member: string): string => {
  const period = companyInfo.previousPeriod || companyInfo.previous_period;
  if (!period) {
    const cleanMember = member.startsWith('_') ? member.substring(1) : member;
    const memberSuffix = cleanMember ? `_${cleanMember}` : '';
    return `context${memberSuffix}`;
  }

  const formattedOpening = formatDateForContext(period.opening);
  const cleanMember = member.startsWith('_') ? member.substring(1) : member;
  const memberSuffix = cleanMember ? `_${cleanMember}` : '';

  return `asof_${formattedOpening}${memberSuffix}`;
};

const FinancialTableInput: React.FC<{
  item: any;
  column: Column;
  contextRef: string;
  fieldId: string;
  isReadonly: boolean;
  value: string;
  onFieldChange: (name: string, contextRef: string, value: string) => void;
  fieldOccurrence?: number; // Use the testOccurrence count
  onFootnoteClick?: (
    fieldLabel: string,
    fieldName: string,
    contextRef: string,
    qname: string,
    fieldId: string,
    isReadonly: boolean
  ) => void;
}> = ({ item, column, contextRef, fieldId, isReadonly, value, onFieldChange, fieldOccurrence = 1, onFootnoteClick }) => {
  // UNCONTROLLED INPUTS: useRef is single source of truth, DOM mirrors it
  // - Editable fields: Uncontrolled, defaultValue from ref, onChange updates ref
  // - Readonly fields: Calculated by HyperFormula, updates ref then DOM
  const [isFocused, setIsFocused] = useState(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const tooltipTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Error indicators now handled by DOM manipulation in useValidation.ts (no React rendering needed)
  
  // Note: defaultValue prop handles initial value from ref (single source of truth)
  // No need to manually set input.value - React handles defaultValue automatically

  // Format value for display
  const formatValue = useCallback((val: string, isReadonlyField: boolean): string => {
    if (!val || val.trim() === '') {
      // Only readonly fields show 0.00 by default, editable fields stay empty
      return isReadonlyField ? '0.00' : '';
    }
    
    const numericValue = numeral(val).value();
    if (numericValue === null || isNaN(numericValue)) {
      return isReadonlyField ? '0.00' : '';
    }
    
    return numeral(numericValue).format('0,0.00');
  }, []);

  // Update DOM value when prop changes (for readonly calculated fields like SOPL/SOCI/ClosingCash)
  // Pure DOM approach - directly update input.value, no React state
  useEffect(() => {
    if (isReadonly && inputRef.current && value) {
      // Direct DOM update - pure DOM like 1.0
      inputRef.current.value = value;
    }
  }, [value, isReadonly]);
  

  // Get field name based on occurrence and attributable fields (like your 1.0 system)
  const getFieldName = useCallback(() => {
    // Check for attributable fields (ignoring occurrence)
    if (ATTRIBUTABLE_FIELDS.includes(item.name)) {
      return `Attributable-${item.name}`;
    }
    
    // Check for Cash Flow fields with occurrence-based naming
    // Note: IncreaseDecreaseInCashAndCashEquivalents is handled by HyperFormula (readonly)
    if (CASH_FLOW_FIELDS.includes(item.name)) {
      if (fieldOccurrence === 1) {
        return `OpeningCash-${item.name}`;
      } else if (fieldOccurrence === 2) {
        return `ClosingCash-${item.name}`;
      }
    }
    
    // Check for occurrence-based fields
    if (item.name === 'ProfitLoss' && fieldOccurrence === 2) {
      return `SOPL-${item.name}`;
    }
    if (item.name === 'ComprehensiveIncome' && fieldOccurrence === 2) {
      return `SOCI-${item.name}`;
    }
    
    return item.name;
  }, [item.name, fieldOccurrence]);

  const handleFocus = useCallback(() => {
    setIsFocused(true);
    // For uncontrolled editable inputs, show raw numeric value when focused (remove formatting)
    if (!isReadonly && inputRef.current) {
      const currentValue = inputRef.current.value || '';
      const numericValue = numeral(currentValue).value();
      if (numericValue !== null) {
        inputRef.current.value = numericValue.toString();
      }
    }
  }, [isReadonly]);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
    
    if (isReadonly) {
      return;
    }
    
    // CRITICAL: Clear any pending debounced update and process immediately
    // This ensures fast typing + blur doesn't lose the value
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    
    // Format the value on blur and update ref immediately (no debounce on blur)
    if (inputRef.current) {
      // Check XBRL mandatory fields first (set 0.00 if empty)
      handleXBRLFieldBlur(inputRef.current);
      
      const currentValue = inputRef.current.value || '';
      const formatted = formatValue(currentValue, false);
      
      // Always update DOM to show formatted value (even if empty)
      if (formatted !== currentValue) {
        inputRef.current.value = formatted;
      }
      
      // ALWAYS update ref on blur - even if empty (to ensure ref is synced)
      // This catches cases where user typed fast and blurred before debounce fired
      const fieldName = getFieldName();
      onFieldChange(fieldName, contextRef, formatted || currentValue || '');
    }
  }, [isReadonly, formatValue, getFieldName, contextRef, onFieldChange]);

  // Get special attributes for HyperFormula
  const getSpecialAttributes = useCallback(() => {
    const attrs: Record<string, string> = {};
    if (item.name === 'ProfitLoss' && fieldOccurrence === 2) {
      attrs['data-sopl-source'] = 'closing';
    }
    if (item.name === 'ComprehensiveIncome' && fieldOccurrence === 2) {
      attrs['data-soci-source'] = 'closing';
    }
    // Add cash source attributes (like 1.0 script)
    if (CASH_FLOW_FIELDS.includes(item.name)) {
      if (fieldOccurrence === 1) {
        attrs['data-cash-source'] = 'opening';
      } else if (fieldOccurrence === 2) {
        attrs['data-cash-source'] = 'closing';
      } else {
        attrs['data-cash-source'] = 'net';
      }
    }
    return attrs;
  }, [item.name, fieldOccurrence]);

  // Prevent non-numeric characters from being entered (only allow numbers, decimal, minus, comma)
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (isReadonly) return;
    
    // Allow: Backspace, Delete, Tab, Escape, Enter, Arrow keys
    if ([8, 9, 27, 13, 46, 37, 38, 39, 40].includes(e.keyCode)) {
      return;
    }
    
    // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+Z
    if ((e.ctrlKey || e.metaKey) && [65, 67, 86, 88, 90].includes(e.keyCode)) {
      return;
    }
    
    // Allow: Home, End
    if ([35, 36].includes(e.keyCode)) {
      return;
    }
    
    // Allow: numbers (0-9) on main keyboard and numpad
    if ((e.keyCode >= 48 && e.keyCode <= 57) || (e.keyCode >= 96 && e.keyCode <= 105)) {
      return;
    }
    
    // Allow: decimal point (.) and comma (,)
    if ([190, 188, 110].includes(e.keyCode)) {
      return;
    }
    
    // Allow: minus sign (-) for negative numbers
    if ([189, 109].includes(e.keyCode)) {
      return;
    }
    
    // Prevent all other keys (including alphabets)
    e.preventDefault();
  }, [isReadonly]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (isReadonly) {
      // Readonly fields shouldn't change
      return;
    }
    
    // UNCONTROLLED: DOM is already updated by user typing
    // Debounce updates to ref and HyperFormula (prevents lag while typing)
    const newValue = e.target.value;
    
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    debounceTimerRef.current = setTimeout(() => {
      const fieldName = getFieldName();
      // DON'T format while typing - just save raw value
      // Formatting ONLY happens on blur
      onFieldChange(fieldName, contextRef, newValue || '');
    }, 300); // 300ms debounce - update ref and HyperFormula after user pauses typing
  }, [getFieldName, contextRef, onFieldChange, isReadonly, formatValue]);
  
  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
    };
  }, []);
  
  // Listen for external DOM updates (like data loading setting input.value directly)
  // For uncontrolled inputs, DOM is already updated, just trigger HyperFormula if needed
  useEffect(() => {
    if (isReadonly || !inputRef.current) return;
    
    const input = inputRef.current;
    
    // Listen for formDataLoaded event - when external scripts set DOM values
    const handleFormDataLoaded = () => {
      // Small delay to let DOM update complete
      setTimeout(() => {
        if (input && input.value && input.value.trim() !== '') {
          const domValue = input.value;
          // Trigger onFieldChange to update HyperFormula (DOM already has the value)
          const fieldName = getFieldName();
          onFieldChange(fieldName, contextRef, domValue);
        }
      }, 100);
    };
    
    document.addEventListener('formDataLoaded', handleFormDataLoaded);
    
    return () => {
      document.removeEventListener('formDataLoaded', handleFormDataLoaded);
    };
  }, [isReadonly, getFieldName, contextRef, onFieldChange]);

  // Check if this field should have a footnote button
  const shouldShowFootnote = !FIELDS_WITHOUT_FOOTNOTE.includes(item.name);

  return (
    <div className="footnote-display-group">
      {shouldShowFootnote ? (
        <div
          className="footnote-anchor"
          data-field-id={fieldId}
          data-field-name={item.name}
          data-field-label={item.standard_label}
          data-context-ref={contextRef}
          data-qname={item.qname}
          data-readonly={isReadonly ? 'true' : 'false'}
        />
      ) : (
        <span className="footnote-button-spacer"></span>
      )}
      <div style={{ position: 'relative', flex: 1 }}>
        <input 
          ref={inputRef}
          type="text" 
          className="form-control financial-input" 
          name={getFieldName()}
          data-qname={fieldOccurrence === 2 ? undefined : item.qname}
          data-context-ref={contextRef}
          data-year-end={getStandardContext(contextRef, column)}
          data-calculation-target={item.name}
          data-no-fill={item.validation?.no_fill || 'N'}
          data-balance={item.properties?.balance}
          data-type={item.properties?.data_type}
          data-period-type={item.properties?.period_type}
          data-field-id={fieldId}
          defaultValue={isReadonly ? (value || '0.00') : (value || '')} // Uncontrolled: use defaultValue for initial value
          readOnly={isReadonly}
          onKeyDown={handleKeyDown}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          style={isReadonly ? { backgroundColor: 'var(--bg-white-dark)', color: '#495057', fontWeight: 'bold' } : {}}
          {...getSpecialAttributes()}
        />
        <ValidationIndicator fieldName={getFieldName()} contextRef={contextRef} />
      </div>
    </div>
  );
};

const LineItem: React.FC<LineItemProps> = ({ 
  item, 
  columns, 
  level, 
  negativeTargets, 
  readOnlyFields, 
  companyInfo,
  formData,
  onFieldChange,
  onGetCalculatedValue,
  getTestOccurrence,
  itemIndex = 0,
  onFootnoteClick,
  duplicateFields,
  onShowNavigationMenu,
  sectionId
}) => {
  // Create a stable unique key for this item based on its index and level
  const itemKey = `${level}_${itemIndex}`;
  
  // Get occurrence for this item (all non-abstract items, any level)
  const testOccurrence = !item.properties?.abstract && getTestOccurrence ? 
    getTestOccurrence(item.name, itemKey) : null;
  if (item.properties?.abstract) {
    return (
      <>
        <tr>
          <td 
            className={`
              ${level === 0 ? 'table-title-0 c-black' : ''}
              ${level === 1 ? 'table-title-1 c-theme-600' : ''}
              ${level === 2 ? 'table-title-2 c-theme-500' : ''}
              ${level === 3 ? 'table-title-3 c-dark' : ''}
              ${level >= 4 ? 'table-title-4 c-light' : ''}
            `}
            colSpan={columns.length + 1}
            style={{ paddingLeft: `${(level + 1) * 15}px` }}
          >
            {level === 4 && '- '}{item.standard_label}
          </td>
        </tr>
        {item.structure?.children && item.structure.children.map((child: any, index: number) => (
           <LineItem
             key={`${child.name}_${child.qname}_${level}_${index}`}
             item={child}
             columns={columns}
             level={level + 1}
             negativeTargets={negativeTargets}
             readOnlyFields={readOnlyFields}
             companyInfo={companyInfo}
             formData={formData}
             onFieldChange={onFieldChange}
             onGetCalculatedValue={onGetCalculatedValue}
             getTestOccurrence={getTestOccurrence}
             itemIndex={index}
             onFootnoteClick={onFootnoteClick}
             duplicateFields={duplicateFields}
             onShowNavigationMenu={onShowNavigationMenu}
             sectionId={sectionId}
           />
        ))}
      </>
    );
  }

  // Compute the actual field name (same logic as in column mapping)
  const getActualFieldName = () => {
    let actualFieldName = item.name;
    if (ATTRIBUTABLE_FIELDS.includes(item.name)) {
      actualFieldName = `Attributable-${item.name}`;
    } else if (CASH_FLOW_FIELDS.includes(item.name)) {
      if (testOccurrence === 1) {
        actualFieldName = `OpeningCash-${item.name}`;
      } else if (testOccurrence === 2) {
        actualFieldName = `ClosingCash-${item.name}`;
      }
    } else if (testOccurrence === 2) {
      if (item.name === 'ProfitLoss') {
        actualFieldName = `SOPL-${item.name}`;
      } else if (item.name === 'ComprehensiveIncome') {
        actualFieldName = `SOCI-${item.name}`;
      }
    }
    return actualFieldName;
  };

  const actualFieldName = getActualFieldName();
  const isDuplicateField = duplicateFields?.has(actualFieldName);

  const getContextRefForColumn = useCallback((column: Column): string => {
    if (CASH_FLOW_FIELDS.includes(item.name) && testOccurrence === 1) {
      if (column.period === 'current') {
        return getCurrentOpeningPeriodContext(item, companyInfo, column.member);
      }
      return getComparativeOpeningPeriodContext(item, companyInfo, column.member);
    }
    return getPeriodContext(item, companyInfo, column.member, column.period);
  }, [item, testOccurrence, companyInfo]);

  const parseNumeric = (raw: string | number | undefined | null): number => {
    if (raw === undefined || raw === null) return 0;
    const clean = String(raw).replace(/,/g, '').replace(/[^\d.-]/g, '');
    const n = parseFloat(clean);
    return Number.isFinite(n) ? n : 0;
  };

  const getColumnFieldValue = (fieldName: string, contextRef: string, standardContext: string): number => {
    const refKey = `${fieldName}_${standardContext}`;
    const refRaw = formData[refKey];
    if (refRaw !== undefined && String(refRaw).trim() !== '') {
      return parseNumeric(refRaw);
    }

    const input = document.querySelector(
      `input[name="${fieldName}"][data-context-ref="${contextRef}"]`
    ) as HTMLInputElement | null;
    if (input && input.value && input.value.trim() !== '') {
      return parseNumeric(input.value);
    }

    return 0;
  };

  const shouldRenderComputedAttributableTotalRow =
    item.name === 'ProfitLossAttributableToNoncontrollingInterests';
  const shouldRenderComputedEquityAndLiabilitiesTotalRow = item.name === 'Equity';

  return (
    <>
      <tr>
        <td 
          className={`
            ${level === 0 ? 'table-title-0 c-black' : ''}
            ${level === 1 ? 'table-title-1 c-theme-600' : ''}
            ${level === 2 ? 'table-title-2 c-theme-500' : ''}
            ${level === 3 ? 'table-title-3 c-dark' : ''}
            ${level >= 4 ? 'table-title-4 c-light' : ''}
            ${negativeTargets.includes(item.name) ? 'negative-indicator' : ''}
          `}
          style={{ paddingLeft: `${(level + 1) * 15}px`, position: 'relative' }}
          title={negativeTargets.includes(item.name) ? 'Subtracted from total' : ''}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>
              {/* Check for Cash Flow fields with custom labels */}
              {CASH_FLOW_FIELDS.includes(item.name as any) && testOccurrence && CASH_FLOW_LABELS[item.name]?.[testOccurrence] 
                ? CASH_FLOW_LABELS[item.name][testOccurrence]
                : item.standard_label}
            </span>
            {/* ✅ REACT-RENDERED: Navigation button (NO imperative DOM manipulation) */}
            {isDuplicateField && onShowNavigationMenu && (
              <button
                type="button"
                className="cross-field-nav-icon"
                title={`Navigate to other instances of ${item.standard_label}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  // Get section ID from closest .sub-content-item
                  const sectionElement = e.currentTarget.closest('.sub-content-item');
                  const currentSectionId = sectionElement?.id || '';
                  if (currentSectionId) {
                    onShowNavigationMenu(actualFieldName, currentSectionId, e.currentTarget);
                  }
                }}
              >
                <span className="nav-text">See details</span>
              </button>
            )}
          </div>
        </td>
        
        {columns.map((column, colIndex) => {
                  const contextRef = getContextRefForColumn(column);
                  
                  const fieldId = `${contextRef}_${item.qname}`;
                  
                  // Get the actual field name that will be used
                  let actualFieldName = item.name;
                  if (ATTRIBUTABLE_FIELDS.includes(item.name)) {
                    actualFieldName = `Attributable-${item.name}`;
                  } else if (CASH_FLOW_FIELDS.includes(item.name)) {
                    if (testOccurrence === 1) {
                      actualFieldName = `OpeningCash-${item.name}`;
                    } else if (testOccurrence === 2) {
                      actualFieldName = `ClosingCash-${item.name}`;
                    }
                  } else if (testOccurrence === 2) {
                    if (item.name === 'ProfitLoss') {
                      actualFieldName = `SOPL-${item.name}`;
                    } else if (item.name === 'ComprehensiveIncome') {
                      actualFieldName = `SOCI-${item.name}`;
                    }
                  }
                  
                  const isClosingField = (actualFieldName.startsWith('SOPL-') && item.name === 'ProfitLoss' && testOccurrence === 2) ||
                                        (actualFieldName.startsWith('SOCI-') && item.name === 'ComprehensiveIncome' && testOccurrence === 2) ||
                                        (actualFieldName.startsWith('ClosingCash-') && CASH_FLOW_FIELDS.includes(item.name) && testOccurrence === 2);
                  
                  const isReadonly = isClosingField ? true :
                    (actualFieldName.startsWith('Attributable-') || actualFieldName.startsWith('OpeningCash-')) ? false :
                    readOnlyFields.has(item.name);
                  
          let fieldValue = '';
          if (isReadonly) {
            const calculatedValue = onGetCalculatedValue(actualFieldName, contextRef);
            fieldValue = calculatedValue !== null && calculatedValue !== undefined ? 
              numeral(calculatedValue).format('0,0.00') : '0.00';
          } else {
            const tempEl = document.querySelector(
              `input[name="${actualFieldName}"][data-context-ref="${contextRef}"], select[name="${actualFieldName}"][data-context-ref="${contextRef}"]`
            ) as HTMLInputElement | HTMLSelectElement | null;
            const standardContext = tempEl?.getAttribute('data-year-end');
            if (standardContext) {
              const refKey = `${actualFieldName}_${standardContext}`;
              fieldValue = formData[refKey] || '';
            }
            if (!fieldValue && item.qname) {
              const qnameKey = `${item.qname}_${contextRef}`;
              fieldValue = formData[qnameKey] || '';
            }
          }
          
          return (
            <td key={colIndex} className="table-input">
              <div className={`
                ${level === 0 ? 'table-title-0 c-black' : ''}
                ${level === 1 ? 'table-title-1 c-theme-600' : ''}
                ${level === 2 ? 'table-title-2 c-theme-500' : ''}
                ${level === 3 ? 'table-title-3 c-dark' : ''}
                ${level >= 4 ? 'table-title-4 c-light' : ''}
              `}>
                 {/* Monetary / Per Share inputs */}
                 {(item.properties?.data_type === 'monetary' || item.properties?.data_type === 'per_share') && (
                   <FinancialTableInput
                     item={item}
                     column={column}
                     contextRef={contextRef}
                     fieldId={fieldId}
                     isReadonly={isReadonly}
                     value={fieldValue}
                     onFieldChange={onFieldChange}
                     fieldOccurrence={testOccurrence || 1}
                     onFootnoteClick={onFootnoteClick}
                   />
                 )}
                 
                 {/* Enumeration dropdown (e.g., Yes/No) */}
                 {typeof item.properties?.data_type === 'object' && 
                  item.properties?.data_type?.type === 'enumeration' && (
                  <select
                    className="form-control"
                     name={item.name}
                     data-qname={item.qname}
                     data-context-ref={contextRef}
                     data-period-type={item.properties?.period_type}
                     data-type="enumeration"
                     data-year-end={`${column.period}_${column.member}`}
                     ref={(el) => {
                       if (!el) return;
                       const qnameKey = `${item.qname}_${contextRef}`;
                       const stdKey = `${actualFieldName}_${column.period}_${column.member}`;
                       const savedValue = formData[qnameKey] || formData[stdKey] || '';
                       if (savedValue && el.value !== savedValue) {
                         el.value = savedValue;
                       }
                     }}
                     onChange={(e) => {
                       if (onFieldChange) {
                         onFieldChange(item.name, contextRef, e.target.value);
                       }
                     }}
                    style={{ width: '100%', minWidth: 0, maxWidth: '100%', boxSizing: 'border-box' }}
                   >
                     <option value="">-- Select --</option>
                     {(item.properties?.data_type?.values || []).map((val: string) => (
                       <option key={val} value={val}>{val}</option>
                     ))}
                   </select>
                 )}
              </div>
            </td>
          );
        })}
      </tr>

      {shouldRenderComputedAttributableTotalRow && (
        <tr>
          <td
            className={`
              ${level === 0 ? 'table-title-0 c-black' : ''}
              ${level === 1 ? 'table-title-1 c-theme-600' : ''}
              ${level === 2 ? 'table-title-2 c-theme-500' : ''}
              ${level === 3 ? 'table-title-3 c-dark' : ''}
              ${level >= 4 ? 'table-title-4 c-light' : ''}
            `}
            style={{ paddingLeft: `${(level + 1) * 15}px`, position: 'relative' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>Attributable total (owners + non-controlling interests)</span>
            </div>
          </td>

          {columns.map((column, colIndex) => {
            const contextRef = getContextRefForColumn(column);
            const standardContext = getStandardContext(contextRef, column);

            const ownerCandidates = [
              'Attributable-ProfitLossAttributableToOwnersOfCompany',
              'Attributable-ProfitLossAttributableToOwnersOfParent',
            ];

            const hasOwnerField = (fieldName: string): boolean => {
              const refKey = `${fieldName}_${standardContext}`;
              const inRef = formData[refKey] !== undefined;
              const inDom = document.querySelector(
                `input[name="${fieldName}"][data-context-ref="${contextRef}"]`
              ) !== null;
              return inRef || inDom;
            };

            const selectedOwnerField = ownerCandidates.find(hasOwnerField) || ownerCandidates[0];
            const ownersValue = getColumnFieldValue(selectedOwnerField, contextRef, standardContext);
            const nciValue = getColumnFieldValue(
              'Attributable-ProfitLossAttributableToNoncontrollingInterests',
              contextRef,
              standardContext
            );

            const computedTotal = ownersValue + nciValue;
            const displayValue = numeral(computedTotal).format('0,0.00');

            return (
              <td key={`computed_total_${colIndex}`} className="table-input">
                <div className={`
                  ${level === 0 ? 'table-title-0 c-black' : ''}
                  ${level === 1 ? 'table-title-1 c-theme-600' : ''}
                  ${level === 2 ? 'table-title-2 c-theme-500' : ''}
                  ${level === 3 ? 'table-title-3 c-dark' : ''}
                  ${level >= 4 ? 'table-title-4 c-light' : ''}
                `}>
                  <div className="footnote-display-group">
                    <span className="footnote-button-spacer"></span>
                    <div style={{ position: 'relative', flex: 1 }}>
                      <input
                        type="text"
                        className="form-control financial-input"
                        name="Computed-ProfitLossAttributableTotal"
                        data-context-ref={contextRef}
                        data-year-end={standardContext}
                        value={displayValue}
                        readOnly={true}
                        style={{ backgroundColor: 'var(--bg-white-dark)', color: '#495057', fontWeight: 'bold' }}
                      />
                      <ValidationIndicator fieldName="Computed-ProfitLossAttributableTotal" contextRef={contextRef} />
                    </div>
                  </div>
                </div>
              </td>
            );
          })}
        </tr>
      )}

      {shouldRenderComputedEquityAndLiabilitiesTotalRow && (
        <tr>
          <td
            className={`
              ${level === 0 ? 'table-title-0 c-black' : ''}
              ${level === 1 ? 'table-title-1 c-theme-600' : ''}
              ${level === 2 ? 'table-title-2 c-theme-500' : ''}
              ${level === 3 ? 'table-title-3 c-dark' : ''}
              ${level >= 4 ? 'table-title-4 c-light' : ''}
            `}
            style={{ paddingLeft: `${(level + 1) * 15}px`, position: 'relative' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>Equity and liabilities total</span>
            </div>
          </td>

          {columns.map((column, colIndex) => {
            const contextRef = getContextRefForColumn(column);
            const standardContext = getStandardContext(contextRef, column);
            const liabilitiesValue = getColumnFieldValue('Liabilities', contextRef, standardContext);
            const equityValue = getColumnFieldValue('Equity', contextRef, standardContext);
            const computedTotal = liabilitiesValue + equityValue;
            const displayValue = numeral(computedTotal).format('0,0.00');

            return (
              <td key={`computed_equity_liabilities_total_${colIndex}`} className="table-input">
                <div className={`
                  ${level === 0 ? 'table-title-0 c-black' : ''}
                  ${level === 1 ? 'table-title-1 c-theme-600' : ''}
                  ${level === 2 ? 'table-title-2 c-theme-500' : ''}
                  ${level === 3 ? 'table-title-3 c-dark' : ''}
                  ${level >= 4 ? 'table-title-4 c-light' : ''}
                `}>
                  <div className="footnote-display-group">
                    <span className="footnote-button-spacer"></span>
                    <div style={{ position: 'relative', flex: 1 }}>
                      <input
                        type="text"
                        className="form-control financial-input"
                        name="Computed-EquityAndLiabilitiesTotal"
                        data-context-ref={contextRef}
                        data-year-end={standardContext}
                        value={displayValue}
                        readOnly={true}
                        style={{ backgroundColor: 'var(--bg-white-dark)', color: '#495057', fontWeight: 'bold' }}
                      />
                      <ValidationIndicator fieldName="Computed-EquityAndLiabilitiesTotal" contextRef={contextRef} />
                    </div>
                  </div>
                </div>
              </td>
            );
          })}
        </tr>
      )}
    </>
  );
};

interface SOFPTableSGProps {
  section: any;
  companyInfo: {
    consolidated: string;
    first_year: string;
    // Extended companyInfo with period dates
    reporting_period?: {
      opening: string;
      start: string;
      end: string;
    };
    previous_period?: {
      opening: string;
      start: string;
      end: string;
    };
    // Fallback for different structure
    currentPeriod?: {
      opening: string;
      start: string;
      end: string;
    };
    previousPeriod?: {
      opening: string;
      start: string;
      end: string;
    };
  };
  negativeTargets?: string[];
  // Add global HyperFormula props
  globalHyperFormula?: {
    isReady: boolean;
    readOnlyFields: Set<string>;
    handleFieldChange: (fieldName: string, contextRef: string, value: string) => void;
    getCalculatedValue: (fieldName: string, contextRef: string) => number | null;
  };
  onFieldChange?: (name: string, contextRef: string, value: string) => void;
  formData?: Record<string, string>; // Ref values (single source of truth): { "fieldName_standardContext": "value" }
  onFootnoteClick?: (
    fieldLabel: string,
    fieldName: string,
    contextRef: string,
    qname: string,
    fieldId: string,
    isReadonly: boolean
  ) => void;
  // Cross-field navigation props
  duplicateFields?: Set<string>;
  onShowNavigationMenu?: (fieldName: string, sectionId: string, triggerElement: HTMLElement) => void;
  // hasFieldError/getFieldError removed - validation now handled via DOM manipulation
}

export const SOFPTableSG: React.FC<SOFPTableSGProps> = React.memo(({ 
  section, 
  companyInfo, 
  negativeTargets = [],
  globalHyperFormula,
  onFieldChange: globalFieldChange,
  formData: parentFormData,
  onFootnoteClick,
  duplicateFields,
  onShowNavigationMenu
}) => {
  const ANALYSIS_SECTION_CONFIG: Record<string, AnalysisSectionConfig> = {
    DisclosureOfDetailedInformationAboutTradeAndOtherReceivablesLineItems: {
      headerLabel: 'Analysis of Trade and other receivables',
      rows: [
        {
          label: 'Current',
          sourceFieldName: 'TradeAndOtherReceivablesCurrent',
          mirroredFieldName: 'TradeAndOtherReceivablesCurrent',
        },
        {
          label: 'Non-current',
          sourceFieldName: 'TradeAndOtherReceivablesNoncurrent',
          mirroredFieldName: 'TradeAndOtherReceivablesNoncurrent',
        },
        {
          label: 'Total',
          sourceFieldName: 'TradeAndOtherReceivables',
          mirroredFieldName: 'Computed-TradeAndOtherReceivablesTotal',
          computeFromFieldNames: ['TradeAndOtherReceivablesCurrent', 'TradeAndOtherReceivablesNoncurrent'],
        },
      ],
    },
    DisclosureOfDetailedInformationAboutTradeAndOtherPayablesLineItems: {
      headerLabel: 'Analysis of Trade and other payables',
      rows: [
        {
          label: 'Current',
          sourceFieldName: 'TradeAndOtherPayablesCurrent',
          mirroredFieldName: 'TradeAndOtherPayablesCurrent',
        },
        {
          label: 'Non-current',
          sourceFieldName: 'TradeAndOtherPayablesNoncurrent',
          mirroredFieldName: 'TradeAndOtherPayablesNoncurrent',
        },
        {
          label: 'Total',
          sourceFieldName: 'TradeAndOtherPayables',
          mirroredFieldName: 'Computed-TradeAndOtherPayablesTotal',
          computeFromFieldNames: ['TradeAndOtherPayablesCurrent', 'TradeAndOtherPayablesNoncurrent'],
        },
      ],
    },
    DisclosureOfLoansAndBorrowingsLineItems: {
      headerLabel: 'Analysis of Loans and borrowings',
      rows: [
        {
          label: 'Current',
          sourceFieldName: 'CurrentLoansAndBorrowings',
          mirroredFieldName: 'CurrentLoansAndBorrowings',
        },
        {
          label: 'Non-current',
          sourceFieldName: 'NoncurrentLoansAndBorrowings',
          mirroredFieldName: 'NoncurrentLoansAndBorrowings',
        },
        {
          label: 'Total',
          sourceFieldName: 'LoansAndBorrowings',
          mirroredFieldName: 'Computed-LoansAndBorrowingsTotal',
          computeFromFieldNames: ['CurrentLoansAndBorrowings', 'NoncurrentLoansAndBorrowings'],
        },
      ],
    },
  };

  // formData parameter is the ref (single source of truth) - passed from parent
  // parentFormData contains ref values: { "fieldName_standardContext": "value" }
  const [calculationTrigger, setCalculationTrigger] = useState(0);
  
  // Local occurrence counter for testing
  const occurrenceCounterRef = useRef<Map<string, number>>(new Map());
  
  // Get occurrence count for testing (stable - doesn't change on re-renders)
  const getTestOccurrence = useCallback((itemName: string, itemKey: string): number => {
    // Use a unique key that includes item position to ensure stability
    const uniqueKey = `${itemName}_${itemKey}`;
    
    if (!occurrenceCounterRef.current.has(uniqueKey)) {
      // Only increment the counter for this item name when we see a new unique key
      const itemCount = Array.from(occurrenceCounterRef.current.keys())
        .filter(key => key.startsWith(`${itemName}_`)).length + 1;
      
      occurrenceCounterRef.current.set(uniqueKey, itemCount);
    }
    
    return occurrenceCounterRef.current.get(uniqueKey)!;
  }, []);
  
  // Use global HyperFormula if provided, otherwise create local instance
  const localHyperFormula = useHyperFormula(
    globalHyperFormula ? null : { sections: { [section.name]: section } }
  );
  
  const hyperFormula = globalHyperFormula || {
    isReady: localHyperFormula.isReady,
    readOnlyFields: localHyperFormula.readOnlyFields,
    handleFieldChange: localHyperFormula.handleFieldChange,
    getCalculatedValue: localHyperFormula.getCalculatedValue
  };

  // Get calculated value from HyperFormula
  // contextRef here is the REAL XBRL context - we need to convert it to STANDARD context for HyperFormula
  const getCalculatedValue = useCallback((fieldName: string, contextRef: string): number | null => {
    if (!hyperFormula.isReady || !hyperFormula.getCalculatedValue) return null;
    
    // Check if this is a SOPL/SOCI closing field
    // Find the input to get its standard context (data-year-end) for same column
    const targetInput = document.querySelector(
      `input[name="${fieldName}"][data-context-ref="${contextRef}"]`
    ) as HTMLInputElement;
    
    if (!targetInput) {
      return 0;
    }
    
    const standardContext = targetInput.getAttribute('data-year-end');
    if (!standardContext) {
      return 0;
    }
    
    // Find all Attributable- inputs in the same column (same data-year-end)
    const allAttributableInputs = Array.from(
      document.querySelectorAll(`input[name^="Attributable-"][data-year-end="${standardContext}"]`)
    ) as HTMLInputElement[];
    
    if (fieldName.startsWith('SOPL-')) {
      // SOPL: Sum 3 rows above (3 Attributable- fields)
      // Find the row position of SOPL field, then get 3 inputs above it
      const soplRow = targetInput.closest('tr');
      if (!soplRow) return 0;
      
      // Get all rows in the table
      const table = soplRow.closest('table');
      if (!table) return 0;
      
      const allRows = Array.from(table.querySelectorAll('tr'));
      const soplRowIndex = allRows.indexOf(soplRow);
      
      // Get 3 rows above SOPL
      let total = 0;
      for (let i = 1; i <= 3 && soplRowIndex - i >= 0; i++) {
        const rowAbove = allRows[soplRowIndex - i];
        const inputAbove = rowAbove.querySelector(
          `input[name^="Attributable-"][data-year-end="${standardContext}"]`
        ) as HTMLInputElement;
        
        if (inputAbove && inputAbove.value && inputAbove.value.trim() !== '') {
          const numValue = parseFloat(inputAbove.value.replace(/[^\d.-]/g, '')) || 0;
          total += numValue;
        }
      }
      
      return total;
    }
    
    if (fieldName.startsWith('SOCI-')) {
      // SOCI: Sum 2 rows above (2 Attributable- fields)
      // Find the row position of SOCI field, then get 2 inputs above it
      const sociRow = targetInput.closest('tr');
      if (!sociRow) return 0;
      
      // Get all rows in the table
      const table = sociRow.closest('table');
      if (!table) return 0;
      
      const allRows = Array.from(table.querySelectorAll('tr'));
      const sociRowIndex = allRows.indexOf(sociRow);
      
      // Get 2 rows above SOCI
      let total = 0;
      for (let i = 1; i <= 2 && sociRowIndex - i >= 0; i++) {
        const rowAbove = allRows[sociRowIndex - i];
        const inputAbove = rowAbove.querySelector(
          `input[name^="Attributable-"][data-year-end="${standardContext}"]`
        ) as HTMLInputElement;
        
        if (inputAbove && inputAbove.value && inputAbove.value.trim() !== '') {
          const numValue = parseFloat(inputAbove.value.replace(/[^\d.-]/g, '')) || 0;
          total += numValue;
        }
      }
      
      return total;
    }
    
    // Check if this is a ClosingCash field
    // Cash closing calculation: OpeningCash + IncreaseDecreaseInCashAndCashEquivalents
    // All three fields must be in the same column (same data-year-end / standard context)
    if (fieldName.startsWith('ClosingCash-')) {
      // Find the closing cash input to get its standard context (data-year-end)
      const closingInput = document.querySelector(
        `input[name="${fieldName}"][data-context-ref="${contextRef}"]`
      ) as HTMLInputElement;
      
      if (!closingInput) {
        return 0;
      }
      
      const standardContext = closingInput.getAttribute('data-year-end');
      if (!standardContext) {
        return 0;
      }
      
      // Find OpeningCash in the same column (same data-year-end)
      const openingInput = document.querySelector(
        `input[name="OpeningCash-CashAndCashEquivalents"][data-year-end="${standardContext}"]`
      ) as HTMLInputElement;
      
      // Find IncreaseDecreaseInCashAndCashEquivalents in the same column (same data-year-end)
      const increaseDecreaseInput = document.querySelector(
        `input[name="IncreaseDecreaseInCashAndCashEquivalents"][data-year-end="${standardContext}"]`
      ) as HTMLInputElement;
      
      let openingCash = 0;
      let increaseDecrease = 0;
      
      // Get OpeningCash value from DOM (it's an editable field)
      if (openingInput && openingInput.value && openingInput.value.trim() !== '') {
        openingCash = parseFloat(openingInput.value.replace(/[^\d.-]/g, '')) || 0;
      }
      
      // Get IncreaseDecreaseInCashAndCashEquivalents value
      // This is a readonly field calculated by HyperFormula
      if (increaseDecreaseInput && increaseDecreaseInput.value && increaseDecreaseInput.value.trim() !== '') {
        increaseDecrease = parseFloat(increaseDecreaseInput.value.replace(/[^\d.-]/g, '')) || 0;
      } else {
        // Fallback: try to get from HyperFormula using standard context
        const hyperFormulaValue = hyperFormula.getCalculatedValue('IncreaseDecreaseInCashAndCashEquivalents', standardContext);
        increaseDecrease = hyperFormulaValue || 0;
      }
      
      const closingCash = openingCash + increaseDecrease;
      
      return closingCash;
    }
    
    // For normal HyperFormula fields, convert real context to standard context
    // Find the input to get its data-year-end (standard context)
    const input = document.querySelector(
      `input[name="${fieldName}"][data-context-ref="${contextRef}"]`
    ) as HTMLInputElement;
    
    if (input) {
      const standardContext = input.getAttribute('data-year-end');
      if (standardContext) {
        // Call HyperFormula with STANDARD context (for performance)
        return hyperFormula.getCalculatedValue(fieldName, standardContext);
      }
    }
    
    // Fallback: try with real context (shouldn't happen if inputs are properly set up)
    return hyperFormula.getCalculatedValue(fieldName, contextRef);
  }, [hyperFormula.isReady, hyperFormula.getCalculatedValue]);

  // Helper function to update ClosingCash when OpeningCash or IncreaseDecreaseInCashAndCashEquivalents changes
  // forceRefUpdate: When true, always update ref even if DOM value unchanged (for initial load)
  const updateClosingCash = useCallback((standardContext: string, forceRefUpdate: boolean = false) => {
    const closingCashInputs = document.querySelectorAll(
      `input[name^="ClosingCash-"][data-year-end="${standardContext}"]`
    ) as NodeListOf<HTMLInputElement>;
    
    closingCashInputs.forEach(closingInput => {
      const closingFieldName = closingInput.getAttribute('name');
      const closingContextRef = closingInput.getAttribute('data-context-ref');
      
      if (closingFieldName && closingContextRef) {
        const calculatedValue = getCalculatedValue(closingFieldName, closingContextRef);
        
        if (calculatedValue !== null) {
          const formattedValue = numeral(calculatedValue).format('0,0.00');
          
          const domNeedsUpdate = closingInput.value !== formattedValue;
          
          // Update DOM if value changed
          if (domNeedsUpdate) {
            closingInput.value = formattedValue;
          }
          
          // 🔒 FIX: During initial load (forceRefUpdate=true), ALWAYS update ref
          // even if DOM value was already correct (loaded from database)
          // This ensures validation can read the value from ref
          if (domNeedsUpdate || forceRefUpdate) {
            if (globalFieldChange) {
              // Use setTimeout(0) to defer ref update for user interactions
              // Use immediate for initial load
              if (forceRefUpdate) {
                globalFieldChange(closingFieldName, closingContextRef, formattedValue);
              } else {
                setTimeout(() => {
                  globalFieldChange(closingFieldName, closingContextRef, formattedValue);
                }, 0);
              }
            }
          }
        }
      }
    });
  }, [getCalculatedValue, globalFieldChange]);

  // Keep UI-only attributable total row in sync without waiting for React re-render.
  const updateAttributableProfitLossTotal = useCallback((standardContext: string) => {
    const computedInputs = document.querySelectorAll(
      `input[name="Computed-ProfitLossAttributableTotal"][data-year-end="${standardContext}"]`
    ) as NodeListOf<HTMLInputElement>;

    const parseValue = (raw: string): number => {
      if (!raw || raw.trim() === '') return 0;
      return parseFloat(raw.replace(/[^\d.-]/g, '')) || 0;
    };

    computedInputs.forEach((computedInput) => {
      const contextRef = computedInput.getAttribute('data-context-ref');
      if (!contextRef) return;

      const ownerCandidates = [
        'Attributable-ProfitLossAttributableToOwnersOfCompany',
        'Attributable-ProfitLossAttributableToOwnersOfParent',
      ];

      let ownersValue = 0;
      for (const ownerFieldName of ownerCandidates) {
        const ownerInput = document.querySelector(
          `input[name="${ownerFieldName}"][data-year-end="${standardContext}"][data-context-ref="${contextRef}"]`
        ) as HTMLInputElement | null;
        if (ownerInput) {
          ownersValue = parseValue(ownerInput.value);
          break;
        }
      }

      const nciInput = document.querySelector(
        `input[name="Attributable-ProfitLossAttributableToNoncontrollingInterests"][data-year-end="${standardContext}"][data-context-ref="${contextRef}"]`
      ) as HTMLInputElement | null;
      const nciValue = nciInput ? parseValue(nciInput.value) : 0;

      computedInput.value = numeral(ownersValue + nciValue).format('0,0.00');
    });
  }, []);

  // Keep UI-only Equity+Liabilities row in sync without waiting for React re-render.
  const updateEquityAndLiabilitiesTotal = useCallback((standardContext: string) => {
    const computedInputs = document.querySelectorAll(
      `input[name="Computed-EquityAndLiabilitiesTotal"][data-year-end="${standardContext}"]`
    ) as NodeListOf<HTMLInputElement>;

    const parseValue = (raw: string): number => {
      if (!raw || raw.trim() === '') return 0;
      return parseFloat(raw.replace(/[^\d.-]/g, '')) || 0;
    };

    computedInputs.forEach((computedInput) => {
      const contextRef = computedInput.getAttribute('data-context-ref');
      if (!contextRef) return;

      const liabilitiesInput = document.querySelector(
        `input[name="Liabilities"][data-year-end="${standardContext}"][data-context-ref="${contextRef}"]`
      ) as HTMLInputElement | null;
      const equityInput = document.querySelector(
        `input[name="Equity"][data-year-end="${standardContext}"][data-context-ref="${contextRef}"]`
      ) as HTMLInputElement | null;

      const liabilitiesValue = liabilitiesInput ? parseValue(liabilitiesInput.value) : 0;
      const equityValue = equityInput ? parseValue(equityInput.value) : 0;

      computedInput.value = numeral(liabilitiesValue + equityValue).format('0,0.00');
    });
  }, []);

  const handleFieldChange = useCallback((name: string, contextRef: string, value: string) => {
    // contextRef here is the REAL XBRL context (asof_20250630_SeparateMember, etc.)
    // UNCONTROLLED: DOM is already updated, just sync with HyperFormula
    if (globalFieldChange) {
      globalFieldChange(name, contextRef, value);
    }
    
    // For HyperFormula, convert real context to standard context
    // Find the input element to get its data-year-end (standard context)
    if (hyperFormula.isReady && hyperFormula.handleFieldChange) {
      // Find the input to get its standard context
      const input = document.querySelector(
        `input[name="${name}"][data-context-ref="${contextRef}"]`
      ) as HTMLInputElement;
      
      if (input) {
        const standardContext = input.getAttribute('data-year-end');
        if (standardContext) {
          // Call HyperFormula with STANDARD context (for performance)
          hyperFormula.handleFieldChange(name, standardContext, value);
          
          // Pure DOM updates for custom formulas (SOPL/SOCI/ClosingCash) - like useHyperFormula.ts
          // When Attributable- fields change, update SOPL/SOCI in same column
          if (name.startsWith('Attributable-')) {
            const soplInputs = document.querySelectorAll(
              `input[name^="SOPL-"][data-year-end="${standardContext}"]`
            ) as NodeListOf<HTMLInputElement>;
            
            const sociInputs = document.querySelectorAll(
              `input[name^="SOCI-"][data-year-end="${standardContext}"]`
            ) as NodeListOf<HTMLInputElement>;
            
            // Update SOPL fields - update DOM AND ref
            soplInputs.forEach(soplInput => {
              const soplFieldName = soplInput.getAttribute('name');
              const soplContextRef = soplInput.getAttribute('data-context-ref');
              if (soplFieldName && soplContextRef) {
                const calculatedValue = getCalculatedValue(soplFieldName, soplContextRef);
                if (calculatedValue !== null) {
                  const formattedValue = numeral(calculatedValue).format('0,0.00');
                  soplInput.value = formattedValue;
                  // CRITICAL: Store in ref via globalFieldChange so validation can read it
                  if (globalFieldChange) {
                    globalFieldChange(soplFieldName, soplContextRef, formattedValue);
                  }
                  soplInput.dispatchEvent(new CustomEvent('hyperformulaUpdate', {
                    detail: { fieldName: soplFieldName, contextRef: soplContextRef, value: formattedValue }
                  }));
                }
              }
            });
            
            // Update SOCI fields - update DOM AND ref
            sociInputs.forEach(sociInput => {
              const sociFieldName = sociInput.getAttribute('name');
              const sociContextRef = sociInput.getAttribute('data-context-ref');
              if (sociFieldName && sociContextRef) {
                const calculatedValue = getCalculatedValue(sociFieldName, sociContextRef);
                if (calculatedValue !== null) {
                  const formattedValue = numeral(calculatedValue).format('0,0.00');
                  sociInput.value = formattedValue;
                  // CRITICAL: Store in ref via globalFieldChange so validation can read it
                  if (globalFieldChange) {
                    globalFieldChange(sociFieldName, sociContextRef, formattedValue);
                  }
                  sociInput.dispatchEvent(new CustomEvent('hyperformulaUpdate', {
                    detail: { fieldName: sociFieldName, contextRef: sociContextRef, value: formattedValue }
                  }));
                }
              }
            });

            // Update display-only attributable total row in the same column immediately.
            updateAttributableProfitLossTotal(standardContext);
          }
          
          // When OpeningCash changes, update ClosingCash
          if (name.startsWith('OpeningCash-')) {
            updateClosingCash(standardContext);
          }

          // Keep display-only Equity + Liabilities total row in sync in same column.
          updateEquityAndLiabilitiesTotal(standardContext);
          // Keep analysis mirror rows in sync in same column.
          updateAnalysisMirrorRows(standardContext);
        }
      }
    }
  }, [globalFieldChange, hyperFormula.isReady, hyperFormula.handleFieldChange, getCalculatedValue, updateClosingCash, updateAttributableProfitLossTotal, updateEquityAndLiabilitiesTotal, updateAnalysisMirrorRows]);

  useEffect(() => {
    const recomputeAllInjectedTotals = () => {
      const contexts = new Set<string>();
      document.querySelectorAll('input[data-year-end]').forEach((el) => {
        const standardContext = el.getAttribute('data-year-end');
        if (standardContext) contexts.add(standardContext);
      });
      contexts.forEach((ctx) => {
        updateAttributableProfitLossTotal(ctx);
        updateEquityAndLiabilitiesTotal(ctx);
        updateAnalysisMirrorRows(ctx);
      });
    };

    const runLater = () => setTimeout(recomputeAllInjectedTotals, 80);
    document.addEventListener('formDataLoaded', runLater);
    document.addEventListener('specialFieldsCalculated', runLater);
    // Footnote modal updates values through EditFilings.handleFieldChange and emits this event.
    // Recompute injected UI-only totals/mirrors so they stay in sync with footnote-driven edits.
    document.addEventListener('footnoteValueUpdated', runLater);
    return () => {
      document.removeEventListener('formDataLoaded', runLater);
      document.removeEventListener('specialFieldsCalculated', runLater);
      document.removeEventListener('footnoteValueUpdated', runLater);
    };
  }, [updateAttributableProfitLossTotal, updateEquityAndLiabilitiesTotal, updateAnalysisMirrorRows]);



  // Define columns based on company info (exactly like original macro)
  const columns: Column[] = [];
  
  if (companyInfo.consolidated === 'Y') {
    columns.push({
      label: 'Current Year Consolidated',
      member: '_ConsolidatedMember',
      period: 'current'
    });
    
    if (companyInfo.first_year === 'N') {
      columns.push({
        label: 'Previous Year Consolidated',
        member: '_ConsolidatedMember',
        period: 'comparative'
      });
    }
    
    columns.push({
      label: 'Current Year Separate',
      member: '_SeparateMember',
      period: 'current'
    });
    
    if (companyInfo.first_year === 'N') {
      columns.push({
        label: 'Previous Year Separate',
        member: '_SeparateMember',
        period: 'comparative'
      });
    }
  } else {
    columns.push({
      label: 'Current Year',
      member: '_SeparateMember',
      period: 'current'
    });
    
    if (companyInfo.first_year === 'N') {
      columns.push({
        label: 'Previous Year',
        member: '_SeparateMember',
        period: 'comparative'
      });
    }
  }

  // Find the actual line items in the section structure
  let lineItems: any[] = [];
  
  if (section.structure?.children) {
    // If the section itself is a LineItems container, use its children directly
    if (section.name.includes('LineItems')) {
      lineItems = section.structure.children;
    } else {
      // Find line items container (matches original macro logic)
      const lineItemsContainer = section.structure.children.find((child: any) => 
        child.name.includes('LineItems') || child.name.includes('Abstract')
      );
      
      if (lineItemsContainer && lineItemsContainer.structure?.children) {
        lineItems = lineItemsContainer.structure.children;
      } else {
        lineItems = section.structure.children;
      }
    }
  }

  const analysisConfig = ANALYSIS_SECTION_CONFIG[section.name];

  const parseAnalysisNumeric = useCallback((raw: string | undefined | null): number => {
    if (!raw || raw.trim() === '') return 0;
    const parsed = numeral(raw).value();
    return parsed === null || Number.isNaN(parsed) ? 0 : parsed;
  }, []);

  const getMirroredAnalysisValue = useCallback((sourceFieldName: string, column: Column): string => {
    const member = column.member.startsWith('_') ? column.member.substring(1) : column.member;
    const standardContext = `${column.period === 'current' ? 'current' : 'comparative'}_${member}`;

    const refRawValue = String((parentFormData || {})[`${sourceFieldName}_${standardContext}`] || '');
    const domRawValue = (document.querySelector(
      `input[name="${sourceFieldName}"][data-year-end="${standardContext}"]:not([data-analysis-mirror="true"])`
    ) as HTMLInputElement | null)?.value || '';
    const rawValue = refRawValue && refRawValue.trim() !== '' ? refRawValue : domRawValue;

    if (!rawValue || rawValue.trim() === '') return '0.00';
    return numeral(parseAnalysisNumeric(rawValue)).format('0,0.00');
  }, [parentFormData, parseAnalysisNumeric]);

  const getComputedTotalFromRef = useCallback((fieldNames: string[], column: Column): string => {
    const member = column.member.startsWith('_') ? column.member.substring(1) : column.member;
    const standardContext = `${column.period === 'current' ? 'current' : 'comparative'}_${member}`;

    const total = fieldNames.reduce((sum, fieldName) => {
      const refRawValue = String((parentFormData || {})[`${fieldName}_${standardContext}`] || '');
      const domRawValue = (document.querySelector(
        `input[name="${fieldName}"][data-year-end="${standardContext}"]:not([data-analysis-mirror="true"])`
      ) as HTMLInputElement | null)?.value || '';
      const rawValue = refRawValue && refRawValue.trim() !== '' ? refRawValue : domRawValue;
      return sum + parseAnalysisNumeric(rawValue);
    }, 0);

    return numeral(total).format('0,0.00');
  }, [parentFormData, parseAnalysisNumeric]);

  // Keep analysis mirror rows synced even when parent does not re-render on ref mutation.
  function updateAnalysisMirrorRows(targetStandardContext?: string): void {
    const contexts: string[] = targetStandardContext
      ? [targetStandardContext]
      : Array.from(
          new Set(
            Array.from(document.querySelectorAll('input[data-analysis-mirror="true"][data-year-end]'))
              .map(el => el.getAttribute('data-year-end') || '')
              .filter(Boolean)
          )
        );

    contexts.forEach((standardContext) => {
      Object.values(ANALYSIS_SECTION_CONFIG).forEach((cfg) => {
        cfg.rows.forEach((row) => {
          const mirrorInputs = document.querySelectorAll(
            `input[name="${row.mirroredFieldName}"][data-analysis-mirror="true"][data-year-end="${standardContext}"]`
          ) as NodeListOf<HTMLInputElement>;

          let displayValue = '0,0.00';
          if (row.computeFromFieldNames && row.computeFromFieldNames.length > 0) {
            const total = row.computeFromFieldNames.reduce((sum, sourceField) => {
              const refRaw = String((parentFormData || {})[`${sourceField}_${standardContext}`] || '');
              const domRaw = (document.querySelector(
                `input[name="${sourceField}"][data-year-end="${standardContext}"]:not([data-analysis-mirror="true"])`
              ) as HTMLInputElement | null)?.value || '';
              return sum + parseAnalysisNumeric(refRaw && refRaw.trim() !== '' ? refRaw : domRaw);
            }, 0);
            displayValue = numeral(total).format('0,0.00');
          } else {
            const refRaw = String((parentFormData || {})[`${row.sourceFieldName}_${standardContext}`] || '');
            const domRaw = (document.querySelector(
              `input[name="${row.sourceFieldName}"][data-year-end="${standardContext}"]:not([data-analysis-mirror="true"])`
            ) as HTMLInputElement | null)?.value || '';
            displayValue = numeral(parseAnalysisNumeric(refRaw && refRaw.trim() !== '' ? refRaw : domRaw)).format('0,0.00');
          }

          mirrorInputs.forEach((input) => {
            input.value = displayValue;
          });
        });
      });
    });
  }

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">{section.standard_label}</h3>
      </div>
      <div className="card-table">
        <table className="table-element" data-datatable-table="true">
          <thead>
            <tr>
              <th>
                <span className="sort">
                  <span className="sort-label"></span>
                </span>
              </th>
              {columns.map((column, index) => (
                <th key={index}>
                  <span className="sort">
                    <span className="sort-label">{column.label}</span>
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
             {lineItems.map((item: any, index: number) => (
               <LineItem
                 key={`${item.name}_${item.qname}_${index}`}
                 item={item}
                 columns={columns}
                 level={0}
                 negativeTargets={negativeTargets}
                 readOnlyFields={hyperFormula.readOnlyFields}
                 companyInfo={companyInfo}
                 formData={parentFormData || {}}
                 onFieldChange={handleFieldChange}
                 onGetCalculatedValue={getCalculatedValue}
                 getTestOccurrence={getTestOccurrence}
                 itemIndex={index}
                 onFootnoteClick={onFootnoteClick}
                 duplicateFields={duplicateFields}
                 onShowNavigationMenu={onShowNavigationMenu}
                 sectionId={undefined}
               />
             ))}

             {analysisConfig && (
               <>
                 <tr>
                   <td
                     className="table-title-0 c-black"
                     colSpan={columns.length + 1}
                     style={{ paddingLeft: '15px' }}
                   >
                     {analysisConfig.headerLabel}
                   </td>
                 </tr>
                 {analysisConfig.rows.map((row) => (
                   <tr key={`analysis_${row.mirroredFieldName}`}>
                     <td
                       className="table-title-1 c-theme-600"
                       style={{ paddingLeft: '30px', position: 'relative' }}
                     >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span>{row.label}</span>
                        {duplicateFields?.has(row.mirroredFieldName) && onShowNavigationMenu && (
                          <button
                            type="button"
                            className="cross-field-nav-icon"
                            title={`Navigate to other instances of ${row.label}`}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const sectionElement = e.currentTarget.closest('.sub-content-item');
                              const currentSectionId = sectionElement?.id || '';
                              if (currentSectionId) {
                                onShowNavigationMenu(row.mirroredFieldName, currentSectionId, e.currentTarget);
                              }
                            }}
                          >
                            <span className="nav-text">See details</span>
                          </button>
                        )}
                      </div>
                     </td>
                     {columns.map((column, colIndex) => {
                       const member = column.member.startsWith('_') ? column.member.substring(1) : column.member;
                      const realContextRef = getPeriodContext(
                        { properties: { period_type: 'instant' } },
                        companyInfo,
                        column.member,
                        column.period
                      );
                      const contextRef = row.computeFromFieldNames
                        ? realContextRef
                        : `analysis_${column.period}_${member}`;
                       const standardContext = `${column.period === 'current' ? 'current' : 'comparative'}_${member}`;
                      const displayValue = row.computeFromFieldNames
                        ? getComputedTotalFromRef(row.computeFromFieldNames, column)
                        : getMirroredAnalysisValue(row.sourceFieldName, column);
                       return (
                         <td key={`analysis_${row.mirroredFieldName}_${colIndex}`} className="table-input">
                           <div className="table-title-1 c-theme-600">
                             <div className="footnote-display-group">
                               <span className="footnote-button-spacer"></span>
                               <div style={{ position: 'relative', flex: 1 }}>
                                 <input
                                   type="text"
                                   className="form-control financial-input"
                                   name={row.mirroredFieldName}
                                   data-context-ref={contextRef}
                                   data-year-end={standardContext}
                                   data-analysis-mirror="true"
                                   value={displayValue}
                                   readOnly={true}
                                   style={{ backgroundColor: 'var(--bg-white-dark)', color: '#495057', fontWeight: 'bold' }}
                                 />
                                 {row.computeFromFieldNames && (
                                   <ValidationIndicator fieldName={row.mirroredFieldName} contextRef={contextRef} />
                                 )}
                               </div>
                             </div>
                           </div>
                         </td>
                       );
                     })}
                   </tr>
                 ))}
               </>
             )}
          </tbody>
        </table>
      </div>
    </div>
  );
});